import { Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTranslation } from "react-i18next";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ResponsiveCharts from "../ChartsLayout/ResponsiveCharts.jsx";
import CustomGridItem from "../ChartsLayout/CustomGridItem.jsx";
import {
  HiddenTypography,
  BarChartEmpty,
} from "../ChartsLayout/EmptyCharts.jsx";

export default function SleepInsightsBarChart({
  sleepData,
  sleepInsights,
  selectedWeek,
}) {
  const weekStarts = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnds = endOfWeek(selectedWeek, { weekStartsOn: 1 });

  const weekDays = eachDayOfInterval({
    start: weekStarts,
    end: weekEnds,
  });

  const { t } = useTranslation("statistics");

  // console.log("All sleep insights data: 123", sleepData);

  const filteredSleepData = sleepData.map(({ duration, ...rest }) => rest);
  const getSleepDataForADate = (date) => {
    const dateFormatted = format(date, "yyyy-MM-dd");
    const daySleepInsightsData = filteredSleepData.find(
      (data) => data.date === dateFormatted
    );
    if (
      !daySleepInsightsData ||
      (!daySleepInsightsData.light &&
        !daySleepInsightsData.rem &&
        !daySleepInsightsData.deep &&
        !daySleepInsightsData.asleep &&
        !daySleepInsightsData.awake &&
        !daySleepInsightsData.in_bed)
    ) {
      return 0;
    }
    return daySleepInsightsData
      ? {
          // duration: daySleepInsightsData.duration? daySleepInsightsData.duration :0,
          light: daySleepInsightsData.light ? daySleepInsightsData.light : 0,
          rem: daySleepInsightsData.rem ? daySleepInsightsData.rem : 0,
          deep: daySleepInsightsData.deep ? daySleepInsightsData.deep : 0,
          asleep: daySleepInsightsData.asleep ? daySleepInsightsData.asleep : 0,
          awake: daySleepInsightsData.awake ? daySleepInsightsData.awake : 0,
          in_bed: daySleepInsightsData.in_bed ? daySleepInsightsData.in_bed : 0,
        }
      : 0;
  };

  // const weeksMonthsWithData =
  //   calculateWeeksAndMonthsWithData(filteredSleepData);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ResponsiveCharts>
          {({ chartWidth, itemMarkWidthBarChart }) => (
            <CustomGridItem>
              <div ref={sleepInsights}>
                <Typography
                  sx={{
                    margin: {
                      xs: "20px 0",
                      sm: "20px 0",
                      md: "0 0 20px 0",
                      lg: "20px 0 0 20px",
                      xl: "20px",
                    },
                    textAlign: "center",
                  }}
                  variant="h6"
                >
                  {t("charts.sleep.insightsTitle")}
                </Typography>
                {weekDays.some((day) => {
                  const data = getSleepDataForADate(day);
                  return data && Object.values(data).some((val) => val > 0);
                }) ? (
                  <>
                    <HiddenTypography />
                    <BarChart
                      xAxis={[
                        {
                          scaleType: "band",
                          data: weekDays.map((day) => format(day, "EEE")),
                          padding: 0.2,
                        },
                      ]}
                      series={[
                        // {
                        //   data: weekDays.map(
                        //     (day) => getSleepDataForADate(day).duration
                        //   ),
                        //   label: "Sleep Duration (min)",
                        //   stack: "total",
                        // },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).in_bed
                          ),
                          label: t("series.inBedMin"),
                          stack: "total",
                        },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).awake
                          ),
                          label: t("series.awakeMin"),
                          stack: "total",
                        },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).light
                          ),
                          label: t("series.lightSleepMin"),
                          stack: "total",
                        },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).rem
                          ),
                          label: t("series.remSleepMin"),
                          stack: "total",
                        },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).deep
                          ),
                          label: t("series.deepSleepMin"),
                          stack: "total",
                        },
                        {
                          data: weekDays.map(
                            (day) => getSleepDataForADate(day).asleep
                          ),
                          label: t("series.asleepMin"),
                          stack: "total",
                        },
                      ]}
                      width={chartWidth}
                      height={300}
                      slotProps={{
                        legend: {
                          direction: "row",
                          position: { vertical: "top", horizontal: "middle" },
                          padding: -5,
                          itemMarkWidth: itemMarkWidthBarChart,
                          itemMarkHeight: 3,
                          markGap: 5,
                          itemGap: 5,
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <BarChartEmpty
                      weekDays={weekDays}
                      chartWidth={chartWidth}
                    />
                  </>
                )}
              </div>
            </CustomGridItem>
          )}
        </ResponsiveCharts>
      </LocalizationProvider>
    </>
  );
}
